// UC-3: Variant Mapping — map observed external identifiers to product Variants
//
// Read path for the Variant Mapping table: LEFT JOIN identifier_observation
// (UC-1, read-only) against suggestions (UC-2) to compute each row's visible
// status (BR-UC3-4). The workbench hook loads one projected document from
// load-variant-mapping-workbench-v1. fetchVariantMappingRows still pages
// PostgREST so existing tests can drive the same assembler.

import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type {
  VariantMappingRow,
  VariantMappingStatus,
  VariantMappingSuggestedCandidate,
} from '@/shared/types/variantMapping';
import { toMatchKey } from '@/shared/variantMapping/matchKey';
import {
  inheritedMappingRowVisible,
  observationTimestamp,
  type CloneMappingReadFilter,
} from '@/shared/variantMapping/cloneMappingCutoff';
import { fetchAllOrgScopedRows, POSTGREST_MAX_ROWS, type PagedOrgClient } from './pagedOrgSelect';
import { buildVariantIdentityMap, type VariantIdentity } from './resolveVariantIdentity';
import {
  cloneReadFilterFromWorkbench,
  useVariantMappingWorkbench,
} from './useVariantMappingWorkbench';

export type { CloneMappingReadFilter };

interface ObservationRow {
  id: string;
  organisation_id: string;
  source_channel: string;
  ingestion_transport: string;
  source_detail?: string | null;
  interpreted_identifier_type: string | null;
  normalized_identifier_value?: string | null;
  raw_source_label: string;
  raw_as_observed_value: string;
  description_text?: string | null;
  duplicate_of?: string | null;
  asserted_at?: string | null;
  created_at?: string | null;
}

interface SuggestionRow {
  id: string;
  observation_id: string;
  status: string;
  candidates: unknown;
  created_at?: string | null;
}

interface LinkRow {
  identifier_type: string;
  identifier_value: string;
  master_product_id: string;
  description_text?: string | null;
  created_at: string;
  active: boolean;
  resolution_source?: string | null;
}

const KNOWN_RESOLUTION_SOURCES = new Set(['manual', 'auto_matched', 'auto_created']);

// UC-92 (AC-AUTORES-8/BR-AUTORES-7): resolution_source is expected to always
// be one of the three known values once UC-32's migration backfill has run —
// anything else (missing/null/unexpected string) is a contract gap between
// this hook and product_identifier_links, not a valid state to guess a label
// for, so it silently degrades to 'manual' (no sub-label) rather than
// rendering an incorrect Auto-matched/Auto-created badge.
function resolveResolutionSource(raw: unknown, link: LinkRow): 'manual' | 'auto_matched' | 'auto_created' {
  if (typeof raw === 'string' && KNOWN_RESOLUTION_SOURCES.has(raw)) {
    return raw as 'manual' | 'auto_matched' | 'auto_created';
  }
  console.warn(
    `[useVariantMappingRows] link ${link.identifier_type}:${link.identifier_value} has an unexpected resolution_source (${JSON.stringify(raw)}); falling back to 'manual'`,
  );
  return 'manual';
}

export type VariantMappingClient = PagedOrgClient;

function observationIdentifierKey(row: ObservationRow): { type: string; value: string } {
  // Raw observed value is the case-sensitive identity source. Historical
  // normalized values may have been case-folded and must not reconnect a
  // case variant after the selected identifier is unmatched.
  return {
    type: (row.interpreted_identifier_type ?? row.raw_source_label).trim().toLowerCase(),
    value: toMatchKey(row.raw_as_observed_value),
  };
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Rework cycle 3 (human-reported defect, live manual testing): the real
 * candidate payload written by run-matching-suggestion-v1
 * (_shared/matchingEngine.ts's generateCandidates) carries no embedded
 * display_name/code — only `target_master_product_id`, `confidence`, and a
 * `decision_context` envelope. Resolution priority for display_name:
 *   1. decision_context.targetDescription — already-computed target-side
 *      description (Tier 4 substring-in-description candidates).
 *   2. decision_context.sourceDisplayName — despite the field's name, this
 *      is populated from matchingEngine's CatalogueEntry (the target side
 *      catalogue entry being matched against), never from the observation
 *      — see buildDecisionContext in _shared/matchingEngine.ts. Currently
 *      always unpopulated in production (fetchOrgCatalogueReadOnly never
 *      sets CatalogueEntry.sourceDisplayName), but resolved here in case a
 *      future catalogue source populates it.
 *   3. A fresh cross-reference against the org's product_identifier_links
 *      catalog (same resolution useVariantCatalog uses).
 *   4. The 'Unknown variant' placeholder.
 */
function resolveCandidateDisplayName(
  decisionContext: Record<string, unknown> | undefined,
  identity: VariantIdentity | undefined,
): string {
  return (
    nonEmptyString(decisionContext?.targetDescription) ??
    nonEmptyString(decisionContext?.sourceDisplayName) ??
    identity?.display_name ??
    'Unknown variant'
  );
}

function parseCandidates(
  raw: unknown,
  identityByMasterProductId: Map<string, VariantIdentity>,
): VariantMappingSuggestedCandidate[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => {
      const masterProductId = String(c.target_master_product_id ?? c.master_product_id ?? c.masterProductId ?? '');
      const identity = identityByMasterProductId.get(masterProductId);
      const decisionContext =
        c.decision_context && typeof c.decision_context === 'object'
          ? (c.decision_context as Record<string, unknown>)
          : undefined;
      return {
        master_product_id: masterProductId,
        display_name: resolveCandidateDisplayName(decisionContext, identity),
        code: identity?.code ?? String(c.code ?? ''),
        confidence: typeof c.confidence === 'number' ? c.confidence : undefined,
      };
    })
    .filter((c) => c.master_product_id.length > 0);
}

export function assembleVariantMappingRows(
  observationsInput: ObservationRow[],
  suggestionsInput: SuggestionRow[],
  linksInput: LinkRow[],
  cloneReadFilter?: CloneMappingReadFilter,
): VariantMappingRow[] {
  const inheritedVisible = (timestamp: string | null | undefined) =>
    !cloneReadFilter ||
    inheritedMappingRowVisible(timestamp, cloneReadFilter.orgCreatedAt, cloneReadFilter.cutoffDate);

  const observations = observationsInput
    .filter((o) => !o.duplicate_of)
    .filter((o) => inheritedVisible(observationTimestamp(o)));
  const suggestions = suggestionsInput.filter((s) => inheritedVisible(s.created_at));
  // Sorted oldest-first so buildVariantIdentityMap's first-active-link-wins
  // rule deterministically picks the link that was in place when the target
  // master_product_id's identity was originally established, rather than
  // whatever order Postgres happens to return rows in (no ORDER BY on the
  // underlying query). This matters once write_mapping_edit (UC-3 pencil
  // action) repoints a link's master_product_id onto a product that already
  // has other, older links — the older link's identity should win.
  const links = linksInput
    .filter((l) => l.active)
    .filter((l) => inheritedVisible(l.created_at))
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const linkByKey = new Map<string, LinkRow>();
  for (const link of links) {
    linkByKey.set(`${link.identifier_type.trim().toLowerCase()}:${toMatchKey(link.identifier_value)}`, link);
  }

  // Reuses the already-fetched links (no extra round-trip) to resolve each
  // suggested candidate's display identity by target_master_product_id,
  // the same way useVariantCatalog resolves the full catalog.
  const identityByMasterProductId = buildVariantIdentityMap(links);

  const suggestionByObservation = new Map<string, SuggestionRow>();
  for (const suggestion of suggestions) {
    if (suggestion.status === 'pending_review') {
      suggestionByObservation.set(suggestion.observation_id, suggestion);
    }
  }

  return observations.map((obs) => {
    const key = observationIdentifierKey(obs);
    const link = linkByKey.get(`${key.type}:${key.value}`);
    const suggestion = suggestionByObservation.get(obs.id);

    // productName has no dedicated column on identifier_observation — this
    // preserves the uploaded description_text as-is. Per AC-UC163-22/23, blank
    // product names must remain blank and never show the literal column name "sku".
    // Uses nullish coalescing to treat null as empty string while preserving
    // explicit empty strings from uploads.
    const productName = obs.description_text ?? '';
    const suggestedCandidates = suggestion ? parseCandidates(suggestion.candidates, identityByMasterProductId) : [];

    let status: VariantMappingStatus;
    let matchedVariant: VariantMappingRow['matchedVariant'] = null;

    if (link) {
      status = 'matched';
      // Resolve display from the TARGET product's identity (by
      // master_product_id), not from this link's own description_text /
      // identifier_value — those describe the observed identifier itself
      // and never change when write_mapping_edit (UC-3 pencil action)
      // repoints master_product_id onto a different product. Falls back to
      // the link's own fields only when no identity is resolvable (e.g. a
      // freshly auto-created product with no other links yet).
      const targetIdentity = identityByMasterProductId.get(link.master_product_id);
      matchedVariant = {
        master_product_id: link.master_product_id,
        display_name: targetIdentity?.display_name ?? link.description_text ?? productName,
        code: targetIdentity?.code ?? link.identifier_value,
        matched_at: link.created_at,
        resolutionSource: resolveResolutionSource(link.resolution_source, link),
      };
    } else if (suggestedCandidates.length > 0) {
      // Rework cycle 2 (human CAT feedback): a suggestion row existing is
      // not sufficient on its own — a row is only "possible to review" if
      // its suggestion actually carries at least one parsed candidate.
      // Presence/absence of candidates is the whole test; there is no
      // confidence-score threshold.
      status = 'needs_review';
    } else {
      status = 'unmatched';
    }

    return {
      observationId: obs.id,
      organisationId: obs.organisation_id,
      externalIdentifier: obs.raw_as_observed_value,
      productName,
      identifierType: obs.interpreted_identifier_type ?? obs.raw_source_label,
      sourceChannel: obs.source_channel,
      sourceDetail: obs.source_detail,
      ingestionTransport: obs.ingestion_transport,
      status,
      matchedVariant,
      suggestionId: suggestion?.id ?? null,
      suggestedCandidates,
    };
  });
}

export async function fetchVariantMappingRows(
  client: VariantMappingClient,
  organisationId: string,
  pageSize?: number,
  cloneReadFilter?: CloneMappingReadFilter,
): Promise<VariantMappingRow[]> {
  const [observationsResult, suggestionsResult, linksResult] = await Promise.all([
    fetchAllOrgScopedRows<ObservationRow>(client, 'identifier_observation', '*', organisationId, pageSize),
    fetchAllOrgScopedRows<SuggestionRow>(client, 'suggestions', '*', organisationId, pageSize),
    fetchAllOrgScopedRows<LinkRow>(client, 'product_identifier_links', '*', organisationId, pageSize),
  ]);

  if (observationsResult.error) throw new Error(observationsResult.error.message);
  if (suggestionsResult.error) throw new Error(suggestionsResult.error.message);
  if (linksResult.error) throw new Error(linksResult.error.message);

  return assembleVariantMappingRows(
    observationsResult.data ?? [],
    suggestionsResult.data ?? [],
    linksResult.data ?? [],
    cloneReadFilter,
  );
}

export async function fetchOrganisationDesignationDefaults(organisationId: string) {
  const { data, error } = await supabase
    .from('organisation_designation_defaults' as never)
    .select('sold_as_column_key, sold_as_identifier_type, bought_as_column_key, bought_as_identifier_type')
    .eq('organisation_id', organisationId)
    .maybeSingle();
  if (error) throw error;
  return data as {
    sold_as_column_key: string | null;
    sold_as_identifier_type: string | null;
    bought_as_column_key: string | null;
    bought_as_identifier_type: string | null;
  } | null;
}

export interface VariantDesignationOverrideRow {
  master_product_id: string;
  sold_as_column_key: string | null;
  sold_as_identifier_type: string | null;
  bought_as_column_key: string | null;
  bought_as_identifier_type: string | null;
}

/** UC-191 warnings are computed from effective values. This fetch does not store a warning column. */
export async function fetchVariantDesignationOverrides(
  organisationId: string,
  client: PagedOrgClient = supabase as unknown as PagedOrgClient,
  pageSize = POSTGREST_MAX_ROWS,
): Promise<VariantDesignationOverrideRow[]> {
  // Primary key is master_product_id; this table has no id column.
  const { data, error } = await fetchAllOrgScopedRows<VariantDesignationOverrideRow>(
    client,
    'variant_designation_overrides',
    'master_product_id, sold_as_column_key, sold_as_identifier_type, bought_as_column_key, bought_as_identifier_type',
    organisationId,
    pageSize,
    'master_product_id',
  );
  if (error) throw new Error(error.message);
  return data ?? [];
}

export function useVariantMappingRows(organisationId: string | undefined) {
  const workbench = useVariantMappingWorkbench(organisationId);
  const data = useMemo(() => {
    if (!workbench.data) return undefined;
    return assembleVariantMappingRows(
      workbench.data.observations,
      workbench.data.suggestions,
      workbench.data.links,
      cloneReadFilterFromWorkbench(workbench.data.organisation),
    );
  }, [workbench.data]);
  return { ...workbench, data };
}
